import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../../core/common/dataTable/index";
import { getEOfficeData } from "../../../core/data/redux/eOfficeSlice";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const EOfficeList = () => {
    const routes = all_routes;
    const dispatch = useDispatch<AppDispatch>();
    const eOfficeState = useSelector((state: any) => state.eOfficeState);

    const [dataList, setDataList] = useState<any[]>([]);
    const [query, setQuery] = useState<string>("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 10,
    });

    const fetchRecords = (pagination: any, query: string) => {
        dispatch(
            getEOfficeData({
                page: pagination.current,
                limit: pagination.pageSize,
                query,
            })
        );
    };

    const handleRefresh = () => {
        fetchRecords(pagination, query);
    };

    const handlePagination = (newPagination: any, filters?: any, sorter?: any) => {
        setPagination((prev) => {
            const isPageSizeChanged = prev.pageSize !== newPagination.pageSize;
            const updatedPagination = {
                current: isPageSizeChanged ? 1 : newPagination.current || 1,
                pageSize: newPagination.pageSize,
                total: newPagination.total || prev.total,
            };
            fetchRecords(updatedPagination, query);
            return updatedPagination;
        });
    };

    const handleSearch = (searchQuery: string) => {
        setQuery(searchQuery);
        const resetPagination = {
            ...pagination,
            current: 1,
        };
        setPagination(resetPagination);
        fetchRecords(resetPagination, searchQuery);
    };

    useEffect(() => {
        fetchRecords(pagination, query);
    }, []);

    useEffect(() => {
        if (
            eOfficeState.loading === "fulfilled" &&
            eOfficeState.action === "list"
        ) {
            // Handle nested data structure: data.data.data contains { data: [], total, page, limit, totalPages }
            const result = eOfficeState?.data?.data?.data || null;
            if (result) {
                const dataArray = result?.data || (Array.isArray(result) ? result : []);
                const total = Number(result?.total) || 0;
                const currentPage = Number(result?.page) || 1;
                const pageSize = Number(result?.limit) || pagination.pageSize;

                setDataList(Array.isArray(dataArray) ? dataArray : []);
                setPagination({
                    current: currentPage,
                    pageSize: pageSize,
                    total: total,
                });
            } else {
                setDataList([]);
                setPagination({
                    current: 1,
                    pageSize: pagination.pageSize,
                    total: 0,
                });
            }
        } else if (eOfficeState.loading === "rejected") {
            toast.error(eOfficeState?.error || "Failed to fetch e-office requisition requests");
        }
    }, [eOfficeState.loading, eOfficeState.action]);

    const columns = [
        {
            title: "Sr. No.",
            render: (_: any, __: any, index: number) =>
                index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        {
            title: "EmpId",
            dataIndex: "EmpID",
            render: (EmpID: string) => <span>{formatEmpIdForDisplay(EmpID || "")}</span>,
        },
        {
            title: "Employee Name",
            dataIndex: "EmployeeName",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Reference Number",
            dataIndex: "ReferenceNumber",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Designation",
            dataIndex: "Designation",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Office Location",
            dataIndex: "OfficeLocation",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Email ID",
            dataIndex: "EmailID",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Contact Number",
            dataIndex: "ContactNumber",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Mac Address",
            dataIndex: "MacAddress",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Request Status",
            dataIndex: "RequestStatus",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Approver User ID",
            dataIndex: "ApproverUserID",
            render: (text: string) => <span>{formatEmpIdForDisplay(text || "")}</span>,
        },
        {
            title: "Request Creation Date",
            dataIndex: "RequestCreationDate",
            render: (text: string) =>
                text ? new Date(text).toLocaleString() : "N/A",
        },
    ];

    return (
        <>
            <style>
                {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
            </style>
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
                        <div className="my-auto mb-2">
                            <h3 className="page-title mb-1">E-Office</h3>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.adminDashboard}>Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="#">Services</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        E-Office
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                            <div className="mb-2">
                                <button
                                    className="btn btn-primary d-flex align-items-center"
                                    onClick={handleRefresh}
                                    disabled={eOfficeState.loading === "pending"}
                                    title="Refresh"
                                >
                                    <i
                                        className={`ti ti-refresh me-2 ${eOfficeState.loading === "pending" ? "spin" : ""
                                            }`}
                                    />
                                    {eOfficeState.loading === "pending" ? "Refreshing..." : "Refresh"}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                            <h4 className="mb-4 mt-3">E-Office</h4>
                        </div>
                        <div className="card-body p-0 py-3">
                            <Table
                                columns={columns}
                                dataSource={dataList}
                                Selection={false}
                                paginationConfig={pagination}
                                handlePagination={handlePagination}
                                loading={
                                    eOfficeState.loading === "pending" && eOfficeState.action === "list"
                                }
                                handleSearch={handleSearch}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EOfficeList;

